// Context analysis types
export interface ArgumentRange {
  start: number
  end: number
}

export interface FunctionCall {
  name: string
  start: number
  openParen: number
  closeParen: number
  end: number
  argRanges: ArgumentRange[]
}

export interface Context {
  company: string
  dimension: string
  path: string[]
  currentFunction?: any
  currentArgument?: any
  prevArgumentValue?: string
}

export interface ArgumentBounds {
  closingParenPos: number
  argRanges: ArgumentRange[]
}

export interface ContextRule {
  contextType: 'company' | 'dimension'
  sourceArgIndex: number // which argument contains the context value
  affectedArgIndices: number[] // which arguments are affected by this context
}

export interface ContextRules {
  [functionName: string]: ContextRule
}

// Context analysis utility functions interface
interface ContextAnalysis {
  defaultContextRules: ContextRules
  detectFunctionCalls: (input: string) => FunctionCall[]
  findArgumentRanges: (input: string, openParenPos: number) => ArgumentBounds
  findArgumentIndex: (argRanges: ArgumentRange[], cursorPos: number) => number
  extractStringValue: (argText: string) => string | null
  isCursorInAffectedPosition: (call: FunctionCall, rule: ContextRule, cursorPos: number) => boolean
  applyContextRule: (currentContext: Context, call: FunctionCall, rule: ContextRule, code: string) => Context
  analyzeContext: (calls: FunctionCall[], cursorPos: number, code: string, contextRules?: ContextRules) => Context
  getCurrentFunction: (source: string, cursorPos: number, functions: { [key: string]: any }) => any
  getCurrentArgument: (source: string, cursorPos: number, currentFunction: any) => any
  getPrevArgumentValue: (source: string, cursorPos: number) => string | undefined
}

export const contextAnalysis: ContextAnalysis = {
  // Default context rules configuration
  defaultContextRules: {
    // Company context functions
    use_company: { contextType: 'company' as const, sourceArgIndex: 0, affectedArgIndices: [1] },
    eliminate: { contextType: 'company' as const, sourceArgIndex: 0, affectedArgIndices: [1] },
    global_ref: { contextType: 'company' as const, sourceArgIndex: 0, affectedArgIndices: [1] },
    group_account: { contextType: 'company' as const, sourceArgIndex: 0, affectedArgIndices: [1, 2] },

    // Dimension context functions
    use_dimension: { contextType: 'dimension' as const, sourceArgIndex: 0, affectedArgIndices: [1] },

    // Functions that could potentially affect context through their arguments
    // These don't change context but may be context-aware
    account: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    pl: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    bs: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    ref: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    variable: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },

    // External data source functions (potentially context-aware)
    gs: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    onedrive: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },

    // Business data functions (context-aware)
    product: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    product_group: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    product_family: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    customer: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    customer_group: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    receipts: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    sales: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    route: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    workhour: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    project: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    account_entries: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },

    // Calculation functions (inherit context but don't change it)
    delta: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    growth: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    div: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    accumulate: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },

    // Control flow functions (inherit context)
    if: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    max: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] },
    min: { contextType: 'company' as const, sourceArgIndex: -1, affectedArgIndices: [] }
  } as ContextRules,

  // Detect function calls in source code
  detectFunctionCalls: (input: string): FunctionCall[] => {
    const calls: FunctionCall[] = []
    const regex = /([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g
    let match: RegExpExecArray | null

    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(input)) !== null) {
      const functionName = match[1]
      const openParenPos = match.index + match[0].length - 1

      // Find matching closing paren and argument boundaries
      const { closingParenPos, argRanges } = contextAnalysis.findArgumentRanges(input, openParenPos)

      calls.push({
        name: functionName,
        start: match.index,
        openParen: openParenPos,
        closeParen: closingParenPos,
        end: closingParenPos !== -1 ? closingParenPos + 1 : input.length,
        argRanges
      })
    }

    return calls
  },

  // Find argument ranges within parentheses
  findArgumentRanges: (input: string, openParenPos: number): ArgumentBounds => {
    let depth = 0
    let bracketDepth = 0
    let argStart = openParenPos + 1
    const argRanges: ArgumentRange[] = []
    let i = openParenPos

    while (i < input.length) {
      const char = input[i]

      if (char === '(') {
        depth += 1
      } else if (char === ')') {
        depth -= 1
        if (depth === 0) {
          // End of this function call
          if (argStart < i && input.slice(argStart, i).trim()) {
            argRanges.push({ start: argStart, end: i })
          } else if (argStart <= i && argRanges.length === 0) {
            // Add empty argument range for positioning when no args exist yet
            argRanges.push({ start: argStart, end: i })
          }
          return { closingParenPos: i, argRanges }
        }
      } else if (char === '[') {
        bracketDepth += 1
      } else if (char === ']') {
        bracketDepth -= 1
      } else if (char === ',' && depth === 1 && bracketDepth === 0) {
        // Argument separator at the right level (not inside brackets)
        if (argStart < i) {
          argRanges.push({ start: argStart, end: i })
        }
        argStart = i + 1
      }
      i += 1
    }

    // Unclosed function - add current argument even if empty for positioning
    if (argStart <= input.length) {
      argRanges.push({ start: argStart, end: input.length })
    }

    return { closingParenPos: -1, argRanges }
  },

  // Find which argument the cursor is in
  findArgumentIndex: (argRanges: ArgumentRange[], cursorPos: number): number => {
    for (let i = 0; i < argRanges.length; i += 1) {
      const range = argRanges[i]
      if (cursorPos >= range.start && cursorPos < range.end) {
        return i
      }
    }
    // If cursor is not in any range but there are ranges, find the best match
    if (argRanges.length > 0) {
      // Check if cursor is right after an argument (on a comma or boundary)
      for (let i = 0; i < argRanges.length; i += 1) {
        const range = argRanges[i]
        if (cursorPos === range.end) {
          return i // Return the argument that ends at this position
        }
      }
      // Otherwise, assume it's in the first argument (e.g., right after opening parenthesis)
      return 0
    }
    return -1
  },

  // Extract string value from argument text
  extractStringValue: (argText: string): string | null => {
    const trimmed = argText.trim()
    const stringMatch = trimmed.match(/^['"]([^'"]*)['"]/)
    return stringMatch ? stringMatch[1] : null
  },

  // Check if cursor is in affected position for a context rule
  isCursorInAffectedPosition: (call: FunctionCall, rule: ContextRule, cursorPos: number): boolean => {
    // If sourceArgIndex is -1, this function doesn't change context
    if (rule.sourceArgIndex === -1) {
      return false
    }

    // Check if cursor is anywhere within affected arguments (including nested functions)
    for (const affectedArgIndex of rule.affectedArgIndices) {
      const argRange = call.argRanges[affectedArgIndex]
      if (argRange && cursorPos >= argRange.start && cursorPos <= argRange.end) {
        return true
      }
    }

    // Check if cursor is in position for affected arguments (even if they don't exist yet)
    const hasSourceArg = call.argRanges.length > rule.sourceArgIndex
    const isInAffectedPosition =
      hasSourceArg && cursorPos > (call.argRanges[rule.sourceArgIndex]?.end || call.openParen) && cursorPos <= call.end

    return isInAffectedPosition
  },

  // Apply context rule to current context
  applyContextRule: (currentContext: Context, call: FunctionCall, rule: ContextRule, code: string): Context => {
    if (call.argRanges.length <= rule.sourceArgIndex) {
      return currentContext // No source argument available
    }

    const sourceArgRange = call.argRanges[rule.sourceArgIndex]
    const sourceArgText = code.slice(sourceArgRange.start, sourceArgRange.end)
    const contextValue = contextAnalysis.extractStringValue(sourceArgText)

    if (!contextValue) {
      return currentContext // Could not extract context value
    }

    return {
      ...currentContext,
      [rule.contextType]: contextValue
    }
  },

  // Analyze context based on function calls (main entry point)
  analyzeContext: (
    calls: FunctionCall[],
    cursorPos: number,
    code: string,
    contextRules: ContextRules = contextAnalysis.defaultContextRules
  ): Context => {
    let currentContext: Context = { company: 'default', dimension: 'default', path: [] }

    // Sort calls by start position to process them in order
    const sortedCalls = [...calls].sort((a, b) => a.start - b.start)

    // Find the deepest nested function call that contains the cursor
    const containingCalls = sortedCalls.filter(call => {
      // For unclosed functions (closeParen === -1), allow cursor at the end
      // For closed functions, use exclusive end boundary
      const endCheck = call.closeParen === -1 ? cursorPos <= call.end : cursorPos < call.end
      return cursorPos > call.openParen && endCheck
    })

    // Process containing calls from outermost to innermost
    containingCalls.forEach(call => {
      currentContext.path = [...currentContext.path, `${call.name}()`]

      // Check if this function has a context rule
      const rule = contextRules[call.name]
      if (rule && contextAnalysis.isCursorInAffectedPosition(call, rule, cursorPos)) {
        currentContext = contextAnalysis.applyContextRule(currentContext, call, rule, code)
      }
    })

    return currentContext
  },

  // Get current function based on cursor position
  getCurrentFunction: (source: string, cursorPos: number, functions: { [key: string]: any }): any => {
    const calls = contextAnalysis.detectFunctionCalls(source)

    // Find the deepest function call that contains the cursor
    const containingCall = calls
      .filter(call => {
        // For unclosed functions (closeParen === -1), allow cursor at the end
        // For closed functions, use exclusive end boundary
        const endCheck = call.closeParen === -1 ? cursorPos <= call.end : cursorPos < call.end
        return cursorPos > call.openParen && endCheck
      })
      .sort((a, b) => b.start - a.start)[0] // Get the innermost (latest starting) call

    if (containingCall && functions[containingCall.name]) {
      return functions[containingCall.name]
    }

    return undefined
  },

  // Get current argument based on cursor position
  getCurrentArgument: (source: string, cursorPos: number, currentFunction: any): any => {
    if (!currentFunction) return undefined

    const calls = contextAnalysis.detectFunctionCalls(source)

    // Find the deepest (innermost) function call that contains the cursor - same logic as getCurrentFunction
    const currentCall = calls
      .filter(call => {
        // For unclosed functions (closeParen === -1), allow cursor at the end
        // For closed functions, use exclusive end boundary
        const endCheck = call.closeParen === -1 ? cursorPos <= call.end : cursorPos < call.end
        return cursorPos > call.openParen && endCheck
      })
      .sort((a, b) => b.start - a.start)[0] // Get the innermost (latest starting) call

    if (!currentCall) return undefined

    // Find which argument the cursor is in
    const argIndex = contextAnalysis.findArgumentIndex(currentCall.argRanges, cursorPos)

    return currentFunction.arguments?.[argIndex] || undefined
  },

  // Get previous argument value based on cursor position
  getPrevArgumentValue: (source: string, cursorPos: number): string | undefined => {
    const calls = contextAnalysis.detectFunctionCalls(source)

    // Find the deepest (innermost) function call that contains the cursor - same logic as getCurrentFunction
    const currentCall = calls
      .filter(call => {
        // For unclosed functions (closeParen === -1), allow cursor at the end
        // For closed functions, use exclusive end boundary
        const endCheck = call.closeParen === -1 ? cursorPos <= call.end : cursorPos < call.end
        return cursorPos > call.openParen && endCheck
      })
      .sort((a, b) => b.start - a.start)[0] // Get the innermost (latest starting) call

    if (!currentCall) return undefined

    // Find which argument the cursor is in
    const currentArgIndex = contextAnalysis.findArgumentIndex(currentCall.argRanges, cursorPos)

    if (currentArgIndex <= 0) return undefined

    // Get the previous argument
    const prevArgRange = currentCall.argRanges[currentArgIndex - 1]
    if (!prevArgRange) return undefined

    const prevArgText = source.slice(prevArgRange.start, prevArgRange.end).trim()

    // Try to extract string value, or return the raw text
    return contextAnalysis.extractStringValue(prevArgText) || prevArgText || undefined
  }
}
